import { createListCollection, Portal, Select, Spinner } from "@chakra-ui/react";
import { useMemo } from "react";

const NONE = "__none__";

const userLabel = (u) =>
    `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email || "Unknown user";

/**
 * Picks the internal admin responsible for a scenario. `users` is the portal-admin list
 * (UserService.getAll). `value` is a user id or null; `onChange` receives a user id or null.
 * Used both inline in the scenarios table and on the scenario detail page — hence the
 * portal `--z-index` override (it renders inside Dialog/Popover hosts too).
 */
const ResponsibleUserSelect = ({
    value,
    onChange,
    users,
    size = "sm",
    width = "220px",
    loading = false,
    placeholder = "Unassigned",
}) => {
    const collection = useMemo(
        () =>
            createListCollection({
                items: [
                    { value: NONE, label: "— Unassigned —" },
                    ...(users ?? []).map((u) => ({ value: u.id, label: userLabel(u) })),
                ],
            }),
        [users],
    );

    return (
        <Select.Root
            collection={collection}
            value={value ? [value] : []}
            onValueChange={(e) => {
                const picked = e.value[0];
                onChange(!picked || picked === NONE ? null : picked);
            }}
            size={size}
            width={width}
            flex="0 0 auto"
            disabled={loading}
        >
            <Select.HiddenSelect />
            <Select.Control>
                <Select.Trigger>
                    <Select.ValueText placeholder={placeholder} css={{ color: "white" }} />
                    {loading && <Spinner size="xs" ml={2} />}
                </Select.Trigger>
                <Select.IndicatorGroup>
                    <Select.Indicator />
                </Select.IndicatorGroup>
            </Select.Control>
            <Portal>
                <Select.Positioner css={{ "--z-index": "1600 !important" }}>
                    <Select.Content>
                        {collection.items.map((item) => (
                            <Select.Item item={item} key={item.value}>
                                {item.label}
                                <Select.ItemIndicator />
                            </Select.Item>
                        ))}
                    </Select.Content>
                </Select.Positioner>
            </Portal>
        </Select.Root>
    );
};

export default ResponsibleUserSelect;
